import type { FC } from 'react';
import React, { useMemo } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { useIntl } from 'react-intl';
import isEqual from 'lodash/isEqual';

import type { ComponentMap } from '@atlassian/forge-ui/renderer/util';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';
import { ForgeUIExtensionAnalyticsContext } from '@atlassian/forge-ui/analytics/ForgeUIAnalyticsContext';
import { ForgeUIRenderer } from '@atlassian/forge-ui/renderer/main';
import { ChromelessEditor } from '@atlassian/forge-ui-kit-editor/chromeless-editor';
import { CommentEditor } from '@atlassian/forge-ui-kit-editor/comment-editor';
import { AdfRenderer } from '@atlassian/forge-ui-kit-adf-renderer/emoji-data-providers';

import { fg } from '@confluence/feature-gating';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getBrowserHistory } from '@confluence/browser-history';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import type { ForgeUIExtensionDataType, RendererProps } from '../types.ts';
import { FORGE_MODULE_PAGE_BANNER } from '../ForgeModuleType.ts';
import { getLocalId } from '../utils/localId.ts';
import { useWorkspaceARI } from '../hooks/useWorkspaceARI.tsx';
import { getMentionProvider } from '../utils/mentionProvider.ts';
import { getFlagProvider } from '../utils/flagProvider.tsx';
import { calculateSize } from '../utils/sizes.ts';
import { recordSuccess, getRecordFailure } from '../utils/recordMetrics.ts';
import { useGetContextToken } from '../hooks/useGetContextToken.tsx';

const useMemoedExtensionData = (
	extensionData: ForgeUIExtensionDataType,
): ForgeUIExtensionDataType => {
	const ref = React.useRef(extensionData);
	if (!isEqual(ref.current, extensionData)) {
		ref.current = extensionData;
	}
	return ref.current;
};

export const Renderer: FC<RendererProps> = ({ app, ...props }) => {
	const localId = getLocalId(app.id);
	const needsInitialBannerHeight =
		app.type === FORGE_MODULE_PAGE_BANNER && props.initialIframeHeight === undefined;
	const isBridgeV2Enabled = needsInitialBannerHeight && fg('forge-ui-bridge-core-reset');
	const initialIframeHeight = props.initialIframeHeight ?? (isBridgeV2Enabled ? 0 : undefined);
	const rendererProps = { ...props, initialIframeHeight };

	return (
		<ForgeUIExtensionAnalyticsContext localId={localId} extensionId={app.id}>
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					//@ts-ignore
					<ForgeExtension {...rendererProps} app={app} localId={localId} flags={flags} />
				)}
			</Subscribe>
		</ForgeUIExtensionAnalyticsContext>
	);
};

type ForgeExtensionProps = RendererProps & {
	localId: string;
	flags: FlagsStateContainer;
	app: Required<Extension>;
	experienceName?: string;
};

const ForgeExtension = ({
	localId,
	flags,
	app,
	extensionData,
	components,
	loadingComponent,
	modalExtension,
	onInitialRender,
	onIframeLoad,
	initialIframeHeight,
	bridge,
	onTearDown,
	dialogs,
	experienceName,
}: ForgeExtensionProps) => {
	const { cloudId, environment, userId } = useSessionData();
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const isLivePage = useIsCurrentPageLive();

	const { showFlag, closeFlag } = getFlagProvider(flags);

	const updatedExtensionData: ForgeUIExtensionDataType = {
		...extensionData,
		content: extensionData.content
			? {
					...extensionData.content,
					subtype: isLivePage ? 'live' : (extensionData.content.subtype ?? null),
				}
			: undefined,
	};

	const memoedExtensionData = useMemoedExtensionData(updatedExtensionData);

	const contextIds = [useWorkspaceARI()];
	const getContextTokenHandler = useGetContextToken({
		extension: app,
		extensionData: memoedExtensionData,
		localId,
		contextIds,
		cachingEnabled: true,
	}); // need to call hooks always.
	const { locale } = useIntl();

	const memoisedComponents = useMemo(() => {
		return {
			...components,
			CommentEditor,
			ChromelessEditor,
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			...(fg('platform-forge-ui-kit-adf-renderer') ? { AdfRenderer } : {}),
		};
	}, [components]);

	return (
		<ForgeUIRenderer
			locale={locale}
			accountId={userId || undefined}
			cloudId={cloudId}
			contextIds={contextIds}
			components={memoisedComponents as ComponentMap}
			extension={app}
			extensionData={memoedExtensionData}
			getContextToken={getContextTokenHandler}
			localId={localId}
			loadingComponent={loadingComponent}
			mentionProvider={getMentionProvider(cloudId, environment, userId)}
			modalExtension={modalExtension}
			product="confluence"
			{...calculateSize(app.type, app?.properties?.viewportSize, app?.properties?.location)}
			bridge={{
				showFlag,
				closeFlag,
				history: getBrowserHistory(),
				...bridge,
			}}
			onInitialRender={onInitialRender}
			onIframeLoad={onIframeLoad}
			initialIframeHeight={initialIframeHeight}
			onConsentModalClose={onTearDown}
			onModalExtensionClose={onTearDown}
			dialogs={dialogs}
			onSuccess={() => recordSuccess(experienceTracker, experienceName)}
			onAppError={getRecordFailure(experienceTracker, experienceName)}
		/>
	);
};
